import React, { useState } from 'react';
import styled from 'styled-components';
import { ModalShell } from '../ui/shell';
import { Btn, Field, Input } from '../ui/primitives';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import { LabelWithHelp } from '../ui/FieldHelp';
import { ENROLLMENT_FIELD_HELP as HELP } from '../student/enrollmentFieldHelp';

const FormRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const GridField = styled(Field)`
  margin-bottom: 0;
`;

const Req = styled.span`
  color: ${({ theme }) => theme.danger};
  margin-left: 2px;
`;

/**
 * Assign-student modal — portal ModalShell + Field/Input; submit logic unchanged.
 */
export default function FormDialog({ open, handleClose, data }) {
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');

  const userId = localStorage.getItem('userId');
  const apiUrl = process.env.REACT_APP_API_URL;

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  const onChange = (e) => {
    const { id, value } = e.target;
    switch (id) {
      case 'firstName':
        setFirstName(value);
        break;
      case 'lastName':
        setLastName(value);
        break;
      case 'studentEmail':
        setStudentEmail(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const payload = {
      courseId: data.courseId,
      studentId: '',
      courseName: data.courseName,
      firstName,
      lastName,
      studentEmail,
    };

    try {
      const response = await fetch(`${apiUrl}/api/studentcourses/${userId}`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to add student course');
      }
      await response.json();
      handleClose();
    } catch (error) {
      console.error('Error adding new student:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <ModalShell
      title="Assign student"
      sub={data?.courseName || undefined}
      onClose={handleClose}
      max="520px"
      foot={(
        <>
          <Btn type="button" $variant="ghost" onClick={handleClose} disabled={loading}>
            Cancel
          </Btn>
          <Btn type="button" $variant="subtle" onClick={handleSubmit} disabled={loading}>
            {loading ? <ButtonSpinnerSlot size={16} /> : 'Submit'}
          </Btn>
        </>
      )}
    >
      <FormRoot>
        <GridField>
          <label htmlFor="firstName">
            Learner first name
            <Req>*</Req>
          </label>
          <Input
            id="firstName"
            value={firstName}
            onChange={onChange}
            placeholder="First name"
          />
        </GridField>
        <GridField>
          <label htmlFor="lastName">
            Learner last name
            <Req>*</Req>
          </label>
          <Input
            id="lastName"
            value={lastName}
            onChange={onChange}
            placeholder="Last name"
          />
        </GridField>
        <GridField>
          <label htmlFor="studentEmail">
            <LabelWithHelp help={HELP.email}>Email</LabelWithHelp>
            <Req>*</Req>
          </label>
          <Input
            id="studentEmail"
            type="email"
            value={studentEmail}
            onChange={onChange}
            placeholder="Email"
          />
        </GridField>
        <GridField>
          <label htmlFor="course_name"><LabelWithHelp help={HELP.courseName}>Course name</LabelWithHelp></label>
          <Input
            id="course_name"
            value={data?.courseName || ''}
            readOnly
          />
        </GridField>
      </FormRoot>
    </ModalShell>
  );
}
